import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getDefaultHeaders } from '../../services/config';
import { usePermissions } from '../../hooks/usePermissions';
import { SystemModule } from '../../types/permissions';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import ImportModal from '../../components/Common/ImportModal';
import { obrasImportConfig } from '../../config/importConfigs';

interface Work {
  id: string;
  numero_obra: string;
  nome: string;
  categoria: string;
  endereco_cidade: string;
  endereco_estado: string;
  valor_total: number;
  moeda: string;
  status: string;
  percentual_conclusao: number;
  cliente_nome: string;
  responsavel_tecnico_nome: string;
  data_inicio_contrato: string;
  data_fim_contrato: string;
}

const Obras: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: obras,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Work>({
    fetchFunction: async (page, limit) => {
      try {
        // Obter headers padrão (inclui token automaticamente)
        const headers = getDefaultHeaders();

        const response = await fetch(buildApiUrl(`/obras?page=${page}&limit=${limit}`), {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            return { success: false, data: [], message: 'Sem permissão' };
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // A API pode retornar um array diretamente ou um objeto com o array dentro
        let obrasArray: any[] = [];

        if (Array.isArray(data)) {
          obrasArray = data;
        } else if (data && typeof data === 'object') {
          // Tentar pegar de diferentes possíveis estruturas
          obrasArray = (data as any).data || (data as any).obras || (data as any).results || [];
        }

        // Mapear os dados da API para o formato esperado pelo componente
        const obrasMapeadas: Work[] = obrasArray.map((obra: any) => ({
          id: String(obra.id),
          numero_obra: obra.numero_obra || '-',
          nome: obra.nome || '-',
          categoria: obra.categoria || 'residencial',
          endereco_cidade: obra.endereco_cidade || '-',
          endereco_estado: obra.endereco_estado || '-',
          valor_total: obra.valor_total || 0,
          moeda: obra.moeda || 'BRL',
          status: obra.status || 'planejamento',
          percentual_conclusao: obra.percentual_conclusao || 0,
          cliente_nome: obra.cliente_nome || '-',
          responsavel_tecnico_nome: obra.responsavel_tecnico_nome || '-',
          data_inicio_contrato: obra.data_inicio_prevista || '',
          data_fim_contrato: obra.data_fim_prevista || ''
        }));

        return {
          success: true,
          data: {
            items: obrasMapeadas,
            pagination: {
              total: (data as any).pagination?.total || obrasMapeadas.length
            }
          }
        };
      } catch (error) {
        console.error('Erro ao carregar obras:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar obras'
        };
      }
    },
    itemsPerPage: 10
  });

  const formatCurrency = (value: number, currency: string = 'BRL'): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value);
  };


  const formatAddress = (cidade?: string, estado?: string): string => {
    if (!cidade && !estado) return '-';
    if (cidade && estado) return `${cidade}/${estado}`;
    return cidade || estado || '-';
  };

  const viewObra = (id: string) => {
    navigate(`/obras/visualizar/${id}`);
  };

  const editObra = (id: string) => {
    navigate(`/obras/editar/${id}`);
  };

  const deleteObra = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta obra?')) {
      return;
    }

    try {
      // TODO: Implementar chamada à API para deletar obra
      console.log('Deletando obra:', id);
      alert('Obra excluída com sucesso!');
      // Recarregar lista após deletar
      window.location.reload();
    } catch (error) {
      console.error('Erro ao deletar obra:', error);
      alert('Erro ao deletar obra. Tente novamente.');
    }
  };

  const createNewObra = () => {
    navigate('/obras/novo');
  };

  if (isLoading) {
    return <div className="loading-spinner-large">Carregando obras...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Obras</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.OBRAS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.OBRAS) && (
            <button
              className="btn btn-primary"
              onClick={createNewObra}
            >
              <i className="fas fa-plus"></i>
              Nova Obra
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <h2>Cadastro de Obras</h2>
          </div>

          <div className="card-body">
            {obras.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building empty-state-icon"></i>
                <h3>Nenhuma obra encontrada</h3>
                <p>Cadastre as primeiras obras da construtora.</p>
                {canCreate(SystemModule.OBRAS) && (
                  <button
                    className="btn btn-primary"
                    onClick={createNewObra}
                  >
                    Cadastrar Primeira Obra
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/* Contador de resultados */}
                {obras.length > 0 && (
                  <ResultsCounter
                    displayedItems={displayedItems}
                    totalItems={totalItems}
                    isLoading={isLoading}
                    label="obras"
                  />
                )}

                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Nome da Obra</th>
                      <th>Categoria</th>
                      <th>Localização</th>
                      <th>Cliente</th>
                      <th>Valor Total</th>
                      <th>Status</th>
                      <th>Progresso</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obras.map(obra => (
                      <tr key={obra.id}>
                        <td>
                          <strong>{obra.numero_obra}</strong>
                        </td>
                        <td>
                          <strong>{obra.nome}</strong>
                          <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                            Resp.: {obra.responsavel_tecnico_nome}
                          </div>
                        </td>
                        <td>
                          <span className="status-badge status-info">
                            {obra.categoria.charAt(0).toUpperCase() + obra.categoria.slice(1)}
                          </span>
                        </td>
                        <td>{formatAddress(obra.endereco_cidade, obra.endereco_estado)}</td>
                        <td>{obra.cliente_nome}</td>
                        <td>{formatCurrency(obra.valor_total, obra.moeda)}</td>
                        <td>
                          <span className={`status-badge status-active`}>
                            Em Andamento
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${obra.percentual_conclusao}%` }}
                              ></div>
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#666' }}>
                              {obra.percentual_conclusao}%
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewObra(obra.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.OBRAS) && (
                              <button
                                className="btn-edit"
                                onClick={() => editObra(obra.id)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.OBRAS) && (
                              <button
                                className="btn-delete"
                                onClick={() => deleteObra(obra.id)}
                                title="Deletar"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas das obras */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-building"></i>
            </div>
            <div className="stat-value">
              {obras.length}
            </div>
            <div className="stat-label">Total de Obras</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-play"></i>
            </div>
            <div className="stat-value">
              {obras.filter(o => o.status === 'em_andamento').length}
            </div>
            <div className="stat-label">Em Andamento</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {obras.filter(o => o.status === 'finalizada').length}
            </div>
            <div className="stat-label">Finalizadas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="stat-value">
              {formatCurrency(obras.reduce((total, obra) => total + obra.valor_total, 0), 'BRL')}
            </div>
            <div className="stat-label">Valor Total</div>
          </div>
        </div>
      </div>
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={obrasImportConfig}
      />
    </div>
  );
};

export default Obras;
